import { createContext, useContext, type ReactNode } from "react";
import type { ViewMode, ExperienceStyle } from "@/lib/views/labels";

interface DynamicViewContextType {
  doctype: string;
  mode: ViewMode;
  style?: ExperienceStyle; // Required when mode === "experience"
  enableSystemTranslations?: boolean; // Default: false
}

const DynamicViewContext = createContext<DynamicViewContextType | undefined>(undefined);

interface DynamicViewProviderProps {
  children: ReactNode;
  doctype: string;
  mode: ViewMode;
  style?: ExperienceStyle;
  enableSystemTranslations?: boolean;
}

/**
 * Provider for Dynamic View context (doctype, mode, style).
 * 
 * This context threads view configuration through the component tree
 * so that FieldRenderer, MetaFieldEditModal, and other components
 * can resolve labels without prop drilling.
 * 
 * @example
 * ```tsx
 * <DynamicViewProvider
 *   doctype={meta.name}
 *   mode="experience"
 *   style="shopify"
 * >
 *   <TabRenderer ... />
 * </DynamicViewProvider>
 * ```
 */
export function DynamicViewProvider({
  children,
  doctype,
  mode,
  style,
  enableSystemTranslations = false,
}: DynamicViewProviderProps) {
  return (
    <DynamicViewContext.Provider
      value={{
        doctype,
        mode,
        style,
        enableSystemTranslations,
      }}
    >
      {children}
    </DynamicViewContext.Provider>
  );
}

/**
 * Hook to access Dynamic View context.
 * 
 * @throws Error if used outside DynamicViewProvider
 * 
 * @example
 * ```tsx
 * const { doctype, mode, style } = useDynamicView();
 * const resolveLabel = useResolveFieldLabel();
 * const result = resolveLabel(doctype, field, mode, style);
 * ```
 */
export function useDynamicView(): DynamicViewContextType {
  const context = useContext(DynamicViewContext);
  if (!context) {
    throw new Error("useDynamicView must be used within DynamicViewProvider");
  }
  return context;
}

